import { useParams } from "react-router-dom";

import {
  Box,
  Button,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowLeftIcon,
  EditPencilIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useUser } from "@js/apps/common/hooks/use-user";
import { EmployerDashboardLayout } from "@js/apps/employer/views/dashboard/layout";
import { openDeleteRosterConfirmModal } from "@js/apps/rosters/components/delete-roster-confirm-modal";
import { openRosterFormModal } from "@js/apps/rosters/components/roster-form-modal";
import { RosterFreelancersTable } from "@js/apps/rosters/components/roster-freelancers-table";
import { RosterVisibilityChip } from "@js/apps/rosters/components/roster-visibility-chip";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch, useNavigate } from "@js/hooks";

import {
  rostersApi,
  useDeleteRosterMutation,
  useGetRosterQuery,
} from "../../api";

export const RosterDetailPage = () => {
  const { id } = useParams();
  const rosterId = Number(id);
  const user = useUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: roster, isLoading } = useGetRosterQuery(rosterId);
  const [deleteRoster] = useDeleteRosterMutation();

  if (isLoading || !roster) {
    return (
      <EmployerDashboardLayout pageTitle="Rosters">
        <Box sx={{ position: "relative", minHeight: "10rem" }}>
          <Loader centered />
        </Box>
      </EmployerDashboardLayout>
    );
  }

  const isOwner = roster.owner === user?.id;
  const canChangeVisibility =
    isOwner || roster.visibility !== ENUMS.RosterVisibility.PUBLIC;

  const handleEditClick = () => {
    openRosterFormModal({ roster, canChangeVisibility });
  };

  const handleDeleteClick = () => {
    openDeleteRosterConfirmModal({
      title: roster.title,
      onConfirm: async () => {
        try {
          await deleteRoster(roster.id).unwrap();
          navigate("/rosters/", { replace: true });
          requestAnimationFrame(() => {
            dispatch(rostersApi.util.invalidateTags([{ type: "Rosters", id }]));
          });
        } catch {
          Snackbar.error("Something went wrong.");
        }
      },
    });
  };

  return (
    <EmployerDashboardLayout
      pageTitle={roster.title}
      bgcolor="var(--soft-violet)"
      setBgOnBody
    >
      <Stack sx={{ gap: 3 }}>
        <Button
          variant="transparent"
          shape="squared"
          size="x-small"
          startIcon={<ArrowLeftIcon sx={{ mb: "1px" }} />}
          onClick={() => navigate("/rosters/")}
          sx={{
            alignSelf: "flex-start",
            ml: -1,
            color: "var(--grey-2) !important",
            fontWeight: "400 !important",
          }}
        >
          See all rosters
        </Button>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 1.5, mb: 1 }}
            >
              <Typography component="h1" variant="title" size="small">
                {roster.title}
              </Typography>
              <RosterVisibilityChip visibility={roster.visibility} />
            </Stack>
            {!!roster.description && (
              <Typography
                component="p"
                variant="paragraph"
                sx={{ overflowWrap: "anywhere" }}
              >
                {roster.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" sx={{ gap: 1 }}>
            <Button
              variant="white-border-beige"
              shape="squared"
              size="medium"
              startIcon={<EditPencilIcon />}
              onClick={handleEditClick}
            >
              Edit
            </Button>
            {isOwner && (
              <Button
                variant="white-border-beige"
                shape="squared"
                size="medium"
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
        <RosterFreelancersTable
          rosterId={roster.id}
          freelancers={roster.freelancers}
        />
      </Stack>
    </EmployerDashboardLayout>
  );
};
